const BASE = '/users/api/auth/oauth/planning-centre';
export class PlanningCentreAuth {
    constructor(http, auth) {
        this.http = http;
        this.auth = auth;
    }
    /**
     * Begin an anonymous PCO sign-in/sign-up flow.
     *
     * @param redirect Post-login navigation target (path the user was on).
     * @param redirectUri OAuth callback URL for THIS frontend, e.g. `${window.location.origin}/auth/oauth/planning-centre/callback`.
     *   Optional — when omitted the backend uses its server-side default (good for legacy single-frontend setups).
     *   When provided it must match an entry in the backend's `PlanningCentreLogin:AllowedRedirectUris` AND be
     *   registered in the PCO OAuth application's allowed Callback URLs.
     */
    async start(redirect, redirectUri) {
        const resp = await this.http.post(`${BASE}/start`, { redirect, redirectUri });
        return this.unwrap(resp);
    }
    /** Complete the anonymous callback. */
    async callback(code, state) {
        const resp = await this.http.post(`${BASE}/callback`, { code, state });
        const raw = this.unwrap(resp);
        return this.normalizeCallback(raw, /* applyToAuth */ true);
    }
    /** Confirm a pending password-required link by re-validating the user's CCMS password. */
    async confirmLink(linkToken, password) {
        const resp = await this.http.post(`${BASE}/confirm-link`, { linkToken, password });
        const user = this.unwrap(resp);
        return this.auth.applyExternalLogin(user);
    }
    /** Begin a "link from profile" flow for an already-signed-in user. */
    async startLink(redirect, redirectUri) {
        const resp = await this.http.post(`${BASE}/link/start`, { redirect, redirectUri });
        return this.unwrap(resp);
    }
    /** Complete a "link from profile" flow. */
    async link(code, state) {
        const resp = await this.http.post(`${BASE}/link`, { code, state });
        this.unwrap(resp);
        return { linked: true };
    }
    /** Remove the PCO link for the signed-in user. */
    async unlink() {
        const resp = await this.http.post(`${BASE}/unlink`, {});
        this.unwrap(resp);
        return { linked: false };
    }
    /** Returns whether the signed-in user has a PCO link. */
    async status() {
        const resp = await this.http.get(`${BASE}/status`);
        return this.unwrap(resp);
    }
    // ----- Helpers -----
    unwrap(resp) {
        if (resp?.success === false) {
            throw new Error(resp.message || 'Planning Centre request failed');
        }
        if (resp?.data == null) {
            throw new Error('Planning Centre request returned no data');
        }
        return resp.data;
    }
    async normalizeCallback(raw, applyToAuth) {
        const isLoggedIn = raw.status === 'LoggedIn' || raw.status === 0;
        if (isLoggedIn) {
            if (!raw.user) {
                throw new Error('Planning Centre callback did not return a user');
            }
            const user = applyToAuth ? await this.auth.applyExternalLogin(raw.user) : raw.user;
            return { status: 'LoggedIn', user };
        }
        if (!raw.linkToken || !raw.email) {
            throw new Error('Planning Centre callback returned LinkRequired without a link token');
        }
        return {
            status: 'LinkRequired',
            linkToken: raw.linkToken,
            email: raw.email,
        };
    }
}
//# sourceMappingURL=PlanningCentreAuth.js.map